import { useEffect } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, ExternalLink, ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { useListing, useSubmitFeedback } from "@/api/listings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FitBadge } from "@/components/FitBadge";
import { NewBadge } from "@/components/NewBadge";
import { Textarea } from "@/components/ui/textarea";
import { formatDate } from "@/lib/utils";

const FeedbackSchema = z.object({
  rating: z.enum(["up", "down"]),
  note: z.string().max(280).optional(),
});

type FeedbackForm = z.infer<typeof FeedbackSchema>;

export function ListingDetail() {
  const { listingId } = useParams({ from: "/listings/$listingId" });
  const id = Number(listingId);
  const { data, isLoading, isError, error } = useListing(id);
  const mutate = useSubmitFeedback(id);

  const { register, handleSubmit, setValue, watch } = useForm<FeedbackForm>({
    resolver: zodResolver(FeedbackSchema),
    defaultValues: { rating: "up", note: "" },
  });

  const rating = watch("rating");

  useEffect(() => {
    if (data) document.title = `${data.title} — Beacon`;
    return () => { document.title = "Beacon"; };
  }, [data?.title]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-6 w-1/2 animate-pulse rounded bg-muted/60" />
        <div className="h-32 animate-pulse rounded-lg bg-muted/40" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {(error as Error | undefined)?.message ?? "Listing not found."}
      </div>
    );
  }

  // Reflect existing feedback into the form on first load
  if (data.feedback && watch("rating") !== data.feedback.rating) {
    setValue("rating", data.feedback.rating);
    setValue("note", data.feedback.note);
  }

  const onSubmit = (values: FeedbackForm) =>
    mutate.mutate(values, {
      onSuccess: () => toast.success("Feedback saved"),
      onError: (e) => toast.error(`Save failed: ${(e as Error).message}`),
    });

  const bestFit = data.evaluations.reduce(
    (max, e) => (e.fit_score > max ? e.fit_score : max),
    -1,
  );

  return (
    <div className="space-y-6">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={14} /> Back to listings
      </Link>

      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight">{data.title}</h1>
          {data.is_new && <NewBadge />}
          {bestFit >= 0 && <FitBadge value={bestFit} />}
        </div>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{data.company_name ?? "Unknown company"}</span>
          {" · "}
          <Badge variant="muted">{data.source}</Badge>
          {data.posted_at && <> · posted <span className="font-mono">{formatDate(data.posted_at, "long")}</span></>}
          {data.application_deadline && <> · deadline <span className="font-mono text-amber-500">{formatDate(data.application_deadline, "long")}</span></>}
          {" · "}
          <a
            href={data.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            original listing <ExternalLink size={12} />
          </a>
        </p>
      </div>

      {data.brief && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Company brief
              <span className="font-mono text-xs text-muted-foreground">{data.brief.model}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm leading-relaxed text-foreground/90">
              {data.brief.summary || "No summary available."}
            </p>
            {data.brief.products.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Products / brands
                </p>
                <ul className="flex flex-wrap gap-1.5">
                  {data.brief.products.map((p) => (
                    <li key={p}>
                      <Badge variant="outline">{p}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>LLM evaluations</CardTitle></CardHeader>
        <CardContent>
          {data.evaluations.length === 0 ? (
            <p className="text-sm text-muted-foreground">Not evaluated yet.</p>
          ) : (
            <>
              <div className="hidden md:block overflow-hidden rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">Criterion</th>
                      <th className="px-3 py-2 font-medium">Match</th>
                      <th className="px-3 py-2 font-medium">Fit</th>
                      <th className="px-3 py-2 font-medium">Confidence</th>
                      <th className="px-3 py-2 font-medium">Reasoning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.evaluations.map((e) => (
                      <tr key={e.id} className="border-t border-border">
                        <td className="px-3 py-2 font-medium">{e.criterion_name ?? `#${e.criterion_id}`}</td>
                        <td className="px-3 py-2">
                          <Badge variant={e.relevant ? "default" : "muted"}>
                            {e.relevant ? "yes" : "no"}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 font-mono tabular-nums">{e.fit_score}%</td>
                        <td className="px-3 py-2 font-mono tabular-nums">{e.confidence}%</td>
                        <td className="px-3 py-2 text-muted-foreground">{e.reasoning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="block md:hidden space-y-3">
                {data.evaluations.map((e) => (
                  <div key={e.id} className="rounded-md border border-border p-3 text-sm space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{e.criterion_name ?? `#${e.criterion_id}`}</span>
                      <Badge variant={e.relevant ? "default" : "muted"}>{e.relevant ? "yes" : "no"}</Badge>
                    </div>
                    <div className="flex gap-3 text-xs text-muted-foreground font-mono">
                      <span>Fit: <span className="text-foreground tabular-nums">{e.fit_score}%</span></span>
                      <span>Confidence: <span className="text-foreground tabular-nums">{e.confidence}%</span></span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{e.reasoning}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your feedback</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-muted-foreground">
            Helps the stats compare the LLM's confidence against actual outcomes.
            {data.feedback && (
              <>
                {" "}Current: <strong className="text-foreground">{data.feedback.rating}</strong>.
              </>
            )}
          </p>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <input type="hidden" {...register("rating")} />
            <div className="flex gap-2">
              <Button
                type="button"
                variant={rating === "up" ? "default" : "outline"}
                onClick={() => setValue("rating", "up")}
              >
                <ThumbsUp size={14} /> Good match
              </Button>
              <Button
                type="button"
                variant={rating === "down" ? "default" : "outline"}
                onClick={() => setValue("rating", "down")}
              >
                <ThumbsDown size={14} /> Not for me
              </Button>
            </div>
            <Textarea
              placeholder="optional note"
              rows={2}
              {...register("note")}
            />
            <Button type="submit" disabled={mutate.isPending}>
              {mutate.isPending ? "Saving…" : "Save feedback"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <details className="rounded-lg border border-border bg-card/50 p-4">
        <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
          Full description
        </summary>
        <pre className="mt-3 whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/90">
          {data.description}
        </pre>
      </details>
    </div>
  );
}
